import { useEffect, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import type { VideoMetadataInput, VideoUpdateInput } from "@vedaunion/shared";
import { useCan } from "../../../lib/auth";
import {
  useAdminVideoDetail,
  useDeleteVideo,
  useSetThumbnail,
  useUpdateVideo,
  videoWatchUrl,
} from "../../../lib/youtube";
import { VideoMetadataForm } from "../../../components/youtube/VideoMetadataForm";
import { CaptionsManager } from "../../../components/youtube/CaptionsManager";
import { YoutubePlayer } from "../../../components/youtube/YoutubePlayer";

/**
 * Single-video edit page. Loads current values via `useAdminVideoDetail`
 * (live read from YouTube + 1 quota unit), shows the shared metadata
 * form pre-filled, plus captions sub-section + danger-zone delete.
 *
 * After save, partial-syncs in the background so the catalog refreshes
 * immediately. Editor stays on the page so they can keep adjusting.
 */

export default function EditVideo() {
  const { id = "" } = useParams<{ id: string }>();
  const [params, setParams] = useSearchParams();
  const justUploaded = params.get("just") === "uploaded";
  const navigate = useNavigate();
  const detailQ = useAdminVideoDetail(id);
  const update = useUpdateVideo(id);
  const setThumb = useSetThumbnail(id);
  const del = useDeleteVideo();
  const canDelete = useCan("youtube.delete");
  const [thumbBlob, setThumbBlob] = useState<Blob | null>(null);
  const [topError, setTopError] = useState<string | null>(null);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [confirmInput, setConfirmInput] = useState("");

  // Strip the just=uploaded flag once seen.
  useEffect(() => {
    if (justUploaded) {
      const next = new URLSearchParams(params);
      next.delete("just");
      setParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [justUploaded]);

  if (!id) return null;

  if (detailQ.isLoading) {
    return (
      <div className="yt-edit-page min-h-[50vh] flex flex-col items-center justify-center text-[var(--color-ink-mute)]">
        <span className="text-4xl mb-4 font-serif">ॐ</span>
        <p className="font-grotesk tracking-[0.15em] uppercase text-xs">Loading video…</p>
      </div>
    );
  }
  if (!detailQ.data) {
    return (
      <p className="yt-admin-empty">
        We couldn't load this video. It may have been deleted.{" "}
        <Link to="/dashboard/youtube" className="row-action">
          Back to videos
        </Link>
      </p>
    );
  }

  const detail = detailQ.data;
  const matchesTitle = confirmInput.trim() === detail.title.trim();

  const defaults: Partial<VideoMetadataInput> = {
    title: detail.title,
    description: detail.description,
    tags: detail.tags,
    categoryId: detail.categoryId,
    privacyStatus: detail.privacyStatus,
    publishAt: detail.publishAt,
    selfDeclaredMadeForKids: detail.selfDeclaredMadeForKids,
    containsSyntheticMedia: detail.containsSyntheticMedia,
    embeddable: detail.embeddable,
    publicStatsViewable: detail.publicStatsViewable,
    license: detail.license,
    defaultLanguage: detail.defaultLanguage,
    defaultAudioLanguage: detail.defaultAudioLanguage,
    recordingDate: detail.recordingDate,
    notifySubscribers: false, // ignored on update — set false to be explicit
    localizations: detail.localizations,
    playlistIds: detail.playlistIds,
  };

  const onSave = async (values: VideoMetadataInput) => {
    setTopError(null);
    try {
      // Send the whole shape — server will only write fields it cares about.
      // VideoUpdateInput is partial<VideoMetadataObject>, so this is fine.
      await update.mutateAsync(values as unknown as VideoUpdateInput);
      if (thumbBlob) {
        await setThumb.mutateAsync(thumbBlob);
        setThumbBlob(null);
      }
    } catch (e) {
      setTopError(
        e instanceof Error ? e.message : "Couldn't save changes.",
      );
    }
  };

  const onDelete = async () => {
    try {
      await del.mutateAsync(id);
      navigate("/dashboard/youtube");
    } catch (e) {
      setTopError(
        e instanceof Error ? e.message : "Couldn't delete the video.",
      );
    }
  };

  return (
    <div className="yt-edit-page">
      {justUploaded && (
        <div className="yt-banner is-ok">
          <p>
            Uploaded successfully. The video is private on YouTube — review
            it, then publish from this page when ready.
          </p>
        </div>
      )}

      <div className="yt-edit-grid">
        <aside className="yt-edit-aside">
          <div className="yt-card-frame">
            <YoutubePlayer kind="video" id={id} title={detail.title} />
          </div>
          <p className="font-grotesk text-[0.62rem] tracking-[0.18em] uppercase text-[var(--color-ink-mute)] mt-3">
            Video ID
          </p>
          <p className="font-grotesk text-[0.78rem] break-all">{id}</p>
          <div className="mt-4 flex flex-col gap-2">
            <a
              href={videoWatchUrl(id)}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-app-ghost"
            >
              Open on YouTube ↗
            </a>
            <a
              href={`https://studio.youtube.com/video/${id}/edit`}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-app-ghost"
            >
              Open in YouTube Studio ↗
            </a>
          </div>
        </aside>

        <div className="yt-edit-form">
          <VideoMetadataForm
            defaultValues={defaults}
            existingThumbnailUrl={detail.thumbnailUrl}
            onThumbnailChange={setThumbBlob}
            onSubmit={onSave}
            submitLabel="Save changes"
            submitting={update.isPending || setThumb.isPending}
            topError={topError}
          />

          <CaptionsManager videoId={id} />

          {canDelete && (
            <section className="yt-form-section yt-danger-zone">
              <h3 className="yt-form-section-title">Danger zone</h3>
              <p className="yt-admin-note">
                Deleting removes the video from YouTube permanently. The
                channel's audit log keeps a record of who deleted it.
              </p>
              {!confirmingDelete ? (
                <button
                  type="button"
                  className="btn-app-ghost is-danger"
                  onClick={() => setConfirmingDelete(true)}
                >
                  Delete this video
                </button>
              ) : (
                <div className="yt-danger-confirm">
                  <p>
                    Type the title{" "}
                    <span className="font-display-italic">
                      "{detail.title}"
                    </span>{" "}
                    to confirm. <em className="font-display-italic">This cannot be undone.</em>
                  </p>
                  <div className="flex gap-2 items-center mt-2 flex-wrap">
                    <input
                      type="text"
                      className="auth-input"
                      value={confirmInput}
                      onChange={(e) => setConfirmInput(e.target.value)}
                      autoFocus
                    />
                    <button
                      type="button"
                      className="btn-app is-danger"
                      disabled={!matchesTitle || del.isPending}
                      onClick={onDelete}
                    >
                      {del.isPending ? "Deleting…" : "Confirm delete"}
                    </button>
                    <button
                      type="button"
                      className="btn-app-ghost"
                      onClick={() => {
                        setConfirmingDelete(false);
                        setConfirmInput("");
                      }}
                      disabled={del.isPending}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </section>
          )}
        </div>
      </div>
    </div>
  );
}
